import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { 
  Users, GraduationCap, Shield, Trash2, Search, 
  Filter, UserPlus, FileText, CheckCircle2, AlertCircle,
  TrendingUp, BookOpen, Clock, MoreVertical, Edit3, User, XCircle,
  Layers, Plus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLoading } from '../hooks/useLoading';
import { useToast } from '../context/ToastContext';
import { Topbar } from '../components/Topbar';
import { Card, Button, Badge, Skeleton, SearchInput } from '../components/ui';
import { motion, AnimatePresence } from 'framer-motion';

export function SecretariaDashboard() {
  // Atualizado para incluir aba Professores Pendentes
  const { user } = useAuth();
  const data = useData();
  const { addToast } = useToast();
  const loading = useLoading(600);
  const [searchParams, setSearchParams] = useSearchParams();

  if (!user || user.type !== 'secretaria' || !data) return null;

  const { 
    allEnrollments, professors, removeEnrollment, removeProfessor, auditLogs,
    classes, addTurma, updateTurma, removeTurma, transferStudentTurma,
    removeAuditLog, clearAllAuditLogs, courses, addCourse, updateCourse, removeCourse,
    approveProfessor, rejectProfessor
  } = data;

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'students');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['students', 'professors', 'pending-professors', 'courses', 'turmas', 'audit'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
    setSearchTerm('');
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [auditFilter, setAuditFilter] = useState('all');

  const filteredStudents = (allEnrollments || []).filter(s => 
    s.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
    s.enrollmentId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProfessors = (professors || []).filter(p => 
    p.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (p.enrollmentId && p.enrollmentId.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredPendingProfessors = (professors || []).filter(p => 
    p.status === 'pending' && (
      p.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (p.enrollmentId && p.enrollmentId.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  );

  const filteredAudit = (auditLogs || []).filter(log => {
    if (auditFilter === 'all') return true;
    return log.action.toLowerCase().includes(auditFilter.toLowerCase());
  }).reverse();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showTurmaModal, setShowTurmaModal] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedTurma, setSelectedTurma] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [turmaFormData, setTurmaData] = useState({ name: '', course: 'Medicina', period: 'Matutino', maxStudents: 20 });
  const [courseFormData, setCourseFormData] = useState({ 
    name: '', 
    description: '', 
    image: null, 
    status: 'Ativo', 
    turmas: [] 
  });

  const handleEdit = (item) => {
    console.log('Abrindo modal para:', item);
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleEditTurma = (turma) => {
    setSelectedTurma(turma);
    setTurmaData({ 
      name: turma.name, 
      course: turma.course || 'Medicina',
      period: turma.period || 'Matutino', 
      maxStudents: turma.maxStudents || 20 
    });
    setShowTurmaModal(true);
  };

  const handleEditCourse = (course) => {
    setSelectedCourse(course);
    setCourseFormData({
      name: course.name,
      description: course.description,
      image: course.image,
      status: course.status,
      turmas: [...course.turmas]
    });
    setShowCourseModal(true);
  };

  const handleDeleteCourse = (id, name) => {
    if (window.confirm(`Tem certeza que deseja remover o curso ${name}?`)) {
      removeCourse(id);
      addToast(`Curso ${name} removido.`, 'success');
    }
  };

  const handleSaveCourse = (e) => {
    e.preventDefault();
    if (selectedCourse) {
      updateCourse(selectedCourse.id, courseFormData);
      addToast('Curso atualizado!', 'success');
    } else {
      addCourse(courseFormData);
      addToast('Novo curso criado!', 'success');
    }
    setShowCourseModal(false);
    setSelectedCourse(null);
    setCourseFormData({ name: '', description: '', image: null, status: 'Ativo', turmas: [] });
  };

  const toggleTurmaSelection = (turmaId) => {
    setCourseFormData(prev => {
      const existingIndex = prev.turmas.findIndex(t => t.turmaId === turmaId);
      if (existingIndex >= 0) {
        // Remove turma
        return { ...prev, turmas: prev.turmas.filter(t => t.turmaId !== turmaId) };
      } else {
        // Add turma with default 20 vacancies
        return { ...prev, turmas: [...prev.turmas, { turmaId, vacancies: 20 }] };
      }
    });
  };

  const updateTurmaVacancies = (turmaId, vacancies) => {
    setCourseFormData(prev => ({
      ...prev,
      turmas: prev.turmas.map(t => t.turmaId === turmaId ? { ...t, vacancies: parseInt(vacancies) || 20 } : t)
    }));
  };

  const handleDeleteTurma = (id, name, course) => {
    if (window.confirm(`Tem certeza que deseja remover a Turma ${name} de ${course}?`)) {
      removeTurma(id);
      addToast(`Turma ${name} removida.`, 'success');
    }
  };

  const handleSaveTurma = (e) => {
    e.preventDefault();
    if (selectedTurma) {
      updateTurma(selectedTurma.id, turmaFormData);
      addToast('Turma atualizada!', 'success');
    } else {
      addTurma(turmaFormData);
      addToast('Nova turma criada!', 'success');
    }
    setShowTurmaModal(false);
    setSelectedTurma(null);
    setTurmaData({ name: '', course: 'Medicina', period: 'Matutino', maxStudents: 20 });
  };

  const handleTransfer = (id, currentTurma, course) => {
    const newTurma = currentTurma === 'A' ? 'B' : 'A';
    if (window.confirm(`Deseja transferir o aluno da Turma ${currentTurma} para a Turma ${newTurma} de ${course}?`)) {
      transferStudentTurma(id, newTurma);
      addToast('Transferência realizada!', 'success');
    }
  };

  const handleDeleteStudent = async (id, name) => {
    if (window.confirm(`Tem certeza que deseja apagar a matrícula de ${name}?`)) {
      await removeEnrollment(id);
      addToast(`Matrícula de ${name} removida.`, 'success');
    }
  };

  const handleDeleteProf = async (id, name) => {
    if (window.confirm(`Tem certeza que deseja remover o registro de ${name}?`)) {
      await removeProfessor(id);
      addToast(`Registro de ${name} removido.`, 'success');
    }
  };

  const handleApproveProf = async (id, name) => {
    if (window.confirm(`Aprovar cadastro de ${name}?`)) {
      await approveProfessor(id);
      addToast(`Professor ${name} aprovado!`, 'success');
    }
  };

  const handleRejectProf = async (id, name) => {
    if (window.confirm(`Rejeitar cadastro de ${name}?`)) {
      await rejectProfessor(id);
      addToast(`Professor ${name} rejeitado.`, 'error');
    }
  };

  const stats = [
    ['Total de Alunos', (allEnrollments || []).length.toString(), 'Matrículas ativas', Users, 'purple'],
    ['Corpo Docente', (professors || []).length.toString(), 'Professores ativos', Shield, 'blue'],
    ['Cursos Ativos', courses.filter(c => c.status === 'Ativo').length.toString(), 'Graduações disponíveis', BookOpen, 'green'],
    ['Logs de Sistema', (auditLogs || []).length.toString(), 'Registros de auditoria', Clock, 'yellow'],
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dashboardWrapper">
      <Topbar />
      
      <section className="welcomeBanner bg-gradient-to-r from-purple/20 to-transparent p-10 rounded-[2.5rem] border border-purple/20 mb-10 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple/10 blur-[80px] rounded-full -mr-32 -mt-32" />
        <div className="relative z-10">
          <h2 className="text-5xl font-black mb-3 tracking-tighter">Painel de Secretaria 🏛️</h2>
          <p className="text-xl text-muted max-w-2xl font-medium">
            Gestão centralizada de alunos, professores e registros acadêmicos da Universidade Lumenfall.
          </p>
        </div>
      </section>

      <section className="stats mb-10">
        {stats.map(([title, value, sub, Icon, color], idx) => (
          <article className={`stat ${color} rounded-[2rem] p-8`} key={idx}>
            {loading ? (
              <div className="w-full">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </div>
            ) : (
              <>
                <div className="statIcon w-16 h-16 rounded-2xl flex items-center justify-center mb-6"><Icon size={32}/></div>
                <div>
                  <h3 className="text-xs font-black uppercase tracking-[2px] opacity-60 mb-1">{title}</h3>
                  <strong className="text-3xl font-black mb-1 block">{value}</strong>
                  <p className="text-xs font-bold opacity-50">{sub}</p>
                </div>
              </>
            )}
          </article>
        ))}
      </section>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col xl:flex-row gap-6 items-center justify-between bg-[#0a1120] p-6 rounded-[2.5rem] border border-[#1d2a42] shadow-xl">
          <div className="flex gap-2 p-1.5 bg-[#070d19] rounded-2xl border border-[#1d2a42] overflow-x-auto no-scrollbar w-full xl:w-auto">
            {[
              { id: 'students', label: 'Alunos', icon: Users },
              { id: 'professors', label: 'Professores', icon: Shield },
              { id: 'pending-professors', label: 'Professores Pendentes', icon: AlertCircle },
              { id: 'courses', label: 'Cursos', icon: BookOpen },
              { id: 'turmas', label: 'Turmas', icon: Layers },
              { id: 'audit', label: 'Auditoria', icon: Clock }
            ].map(tab => (
              <button 
                key={tab.id}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-purple text-white shadow-lg' : 'text-muted hover:text-white hover:bg-white/5'}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>
          
          <div className="w-full xl:w-96">
            <SearchInput 
              placeholder={`Buscar ${activeTab === 'students' ? 'por nome ou matrícula' : activeTab === 'professors' ? 'por nome ou registro' : 'na auditoria'}...`} 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        <Card 
          title={
            activeTab === 'students' ? "Gestão de Alunos" : 
            activeTab === 'professors' ? "Corpo Docente" : 
            activeTab === 'pending-professors' ? "Professores Pendentes de Aprovação" : 
            activeTab === 'courses' ? "Catálogo de Cursos" : 
            activeTab === 'turmas' ? "Gestão de Turmas" :
            "Central de Auditoria"
          } 
          actions={
            activeTab === 'turmas' ? (
              <Button size="sm" className="flex items-center gap-2" onClick={() => { setSelectedTurma(null); setTurmaData({ name: '', course: 'Medicina', period: 'Matutino', maxStudents: 20 }); setShowTurmaModal(true); }}>
                <Plus size={14} /> Nova Turma
              </Button>
            ) : activeTab === 'courses' ? (
              <Button size="sm" className="flex items-center gap-2" onClick={() => { setSelectedCourse(null); setCourseFormData({ name: '', description: '', image: null, status: 'Ativo', turmas: [] }); setShowCourseModal(true); }}>
                <Plus size={14} /> Novo Curso
              </Button>
            ) : activeTab === 'audit' ? (
              <Button size="sm" variant="outline" className="flex items-center gap-2 text-red hover:bg-red/10 border-red/20" onClick={clearAllAuditLogs}>
                <Trash2 size={14} /> Limpar Tudo
              </Button>
            ) : null
          }
          className="tablePanel rounded-[2.5rem] p-8"
        >
          <div className="tableWrap mt-4">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : (
              <table>
                <thead>
                  {activeTab === 'students' && (
                    <tr>
                      <th>Estudante</th>
                      <th>Matrícula</th>
                      <th>Curso</th>
                      <th>Turma</th>
                      <th>Ações</th>
                    </tr>
                  )}
                  {activeTab === 'professors' && (
                    <tr>
                      <th>Docente</th>
                      <th>Registro</th>
                      <th>Disciplinas</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  )}
                  {activeTab === 'pending-professors' && (
                    <tr>
                      <th>Docente</th>
                      <th>Registro</th>
                      <th>Disciplinas</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  )}
                  {activeTab === 'courses' && (
                    <tr>
                      <th>Curso</th>
                      <th>Vagas Totais</th>
                      <th>Ocupação</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  )}
                  {activeTab === 'turmas' && (
                    <tr>
                      <th>Turma</th>
                      <th>Curso</th>
                      <th>Período</th>
                      <th>Alunos</th>
                      <th>Vagas</th>
                      <th>Ações</th>
                    </tr>
                  )}
                  {activeTab === 'audit' && (
                    <tr>
                      <th>Usuário</th>
                      <th>Ação Realizada</th>
                      <th>Data</th>
                      <th>Hora</th>
                      <th>Ações</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {activeTab === 'students' && (filteredStudents.length > 0 ? filteredStudents.map(s => (
                    <tr key={s.enrollmentId}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-purple/10 flex items-center justify-center text-purple shadow-inner">
                            <User size={18} />
                          </div>
                          <div className="flex flex-col">
                            <strong className="text-sm">{s.fullName}</strong>
                            <span className="text-[10px] text-muted font-bold uppercase tracking-widest">{s.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td><span className="font-mono text-xs text-purple font-black">{s.enrollmentId}</span></td>
                      <td><Badge variant="blue" className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest">{s.course}</Badge></td>
                      <td>
                        <button 
                          onClick={() => handleTransfer(s.enrollmentId, s.turma, s.course)}
                          className="flex items-center gap-2 px-3 py-1 rounded-lg bg-white/5 border border-white/10 hover:border-purple/50 transition-all group"
                        >
                          <Badge variant="purple" className="text-[9px] font-black uppercase tracking-widest">Turma {s.turma}</Badge>
                          <TrendingUp size={12} className="text-muted group-hover:text-purple" />
                        </button>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button 
                            className="p-2.5 bg-white/5 rounded-xl hover:text-purple hover:bg-purple/10 transition-all"
                            onClick={() => handleEdit(s)}
                            title="Editar Aluno"
                          >
                            <Edit3 size={18}/>
                          </button>
                          <button 
                            className="p-2.5 bg-white/5 rounded-xl hover:text-red hover:bg-red/10 transition-all"
                            onClick={() => handleDeleteStudent(s.enrollmentId, s.fullName)}
                            title="Remover Matrícula"
                          >
                            <Trash2 size={18}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-center py-20 opacity-30 font-black uppercase tracking-widest">Nenhum aluno localizado</td></tr>
                  ))}

                  {activeTab === 'professors' && (filteredProfessors.length > 0 ? filteredProfessors.map(p => (
                    <tr key={p.enrollmentId}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-purple/10 flex items-center justify-center text-purple shadow-inner">
                            <User size={18} />
                          </div>
                          <div className="flex flex-col">
                            <strong className="text-sm">{p.fullName}</strong>
                            <span className="text-[10px] text-muted font-bold uppercase tracking-widest">{p.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td><span className="font-mono text-xs text-blue font-black">{p.enrollmentId}</span></td>
                      <td><span className="text-[10px] font-black text-muted uppercase tracking-widest">{p.courses || 'N/A'}</span></td>
                      <td>
                        <Badge 
                          variant={
                            p.status === 'approved' ? 'green' : 
                            p.status === 'pending' ? 'yellow' : 
                            'red'
                          } 
                          className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest"
                        >
                          {p.status === 'approved' ? 'Aprovado' : 
                           p.status === 'pending' ? 'Pendente' : 
                           'Rejeitado'}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button 
                            className="p-2.5 bg-white/5 rounded-xl hover:text-blue hover:bg-blue/10 transition-all"
                            onClick={() => handleEdit(p)}
                            title="Editar Professor"
                          >
                            <Edit3 size={18}/>
                          </button>
                          {p.status === 'pending' && (
                            <>
                              <button 
                                className="p-2.5 bg-green/10 rounded-xl hover:text-green hover:bg-green/20 transition-all"
                                onClick={() => handleApproveProf(p.enrollmentId, p.fullName)}
                                title="Aprovar Professor"
                              >
                                <CheckCircle2 size={18}/>
                              </button>
                              <button 
                                className="p-2.5 bg-red/10 rounded-xl hover:text-red hover:bg-red/20 transition-all"
                                onClick={() => handleRejectProf(p.enrollmentId, p.fullName)}
                                title="Rejeitar Professor"
                              >
                                <XCircle size={18}/>
                              </button>
                            </>
                          )}
                          <button 
                            className="p-2.5 bg-white/5 rounded-xl hover:text-red hover:bg-red/10 transition-all"
                            onClick={() => handleDeleteProf(p.enrollmentId, p.fullName)}
                          >
                            <Trash2 size={18}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-center py-20 opacity-30 font-black uppercase tracking-widest">Nenhum professor localizado</td></tr>
                  ))}

                  {activeTab === 'pending-professors' && (filteredPendingProfessors.length > 0 ? filteredPendingProfessors.map(p => (
                    <tr key={p.enrollmentId}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-purple/10 flex items-center justify-center text-purple shadow-inner">
                            <User size={18} />
                          </div>
                          <div className="flex flex-col">
                            <strong className="text-sm">{p.fullName}</strong>
                            <span className="text-[10px] text-muted font-bold uppercase tracking-widest">{p.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td><span className="font-mono text-xs text-blue font-black">{p.enrollmentId}</span></td>
                      <td><span className="text-[10px] font-black text-muted uppercase tracking-widest">{p.courses || 'N/A'}</span></td>
                      <td><Badge variant="yellow" className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest">Pendente</Badge></td>
                      <td>
                        <div className="flex gap-2">
                          <button 
                            className="p-2.5 bg-green/10 rounded-xl hover:text-green hover:bg-green/20 transition-all"
                            onClick={() => handleApproveProf(p.enrollmentId, p.fullName)}
                            title="Aprovar Professor"
                          >
                            <CheckCircle2 size={18}/>
                          </button>
                          <button 
                            className="p-2.5 bg-red/10 rounded-xl hover:text-red hover:bg-red/20 transition-all"
                            onClick={() => handleRejectProf(p.enrollmentId, p.fullName)}
                            title="Rejeitar Professor"
                          >
                            <XCircle size={18}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-center py-20 opacity-30 font-black uppercase tracking-widest">Nenhum professor pendente de aprovação</td></tr>
                  ))}

                  {activeTab === 'courses' && (courses || []).map(c => {
                    const totalVacancies = c.turmas.reduce((sum, t) => sum + t.vacancies, 0);
                    const occupied = (allEnrollments || []).filter(e => e.course === c.name || e.courseName === c.name).length;
                    const percent = totalVacancies > 0 ? (occupied / totalVacancies) * 100 : 0;
                    return (
                      <tr key={c.id}>
                        <td><strong className="text-sm">{c.name}</strong></td>
                        <td><span className="font-mono font-black text-purple">{totalVacancies}</span></td>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden max-w-[120px] border border-white/5">
                              <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: `${percent}%` }}
                                className={`h-full ${percent >= 100 ? 'bg-red' : percent >= 80 ? 'bg-yellow' : 'bg-purple'}`} 
                              />
                            </div>
                            <span className="text-[10px] font-mono font-black">{occupied}/{totalVacancies}</span>
                          </div>
                        </td>
                        <td>
                          <Badge variant={c.status === 'Ativo' ? 'green' : 'red'} className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest">
                            {c.status}
                          </Badge>
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <button 
                              className="p-2.5 bg-white/5 rounded-xl hover:text-purple hover:bg-purple/10 transition-all"
                              onClick={() => handleEditCourse(c)}
                              title="Editar Curso"
                            >
                              <Edit3 size={18}/>
                            </button>
                            <button 
                              className="p-2.5 bg-white/5 rounded-xl hover:text-red hover:bg-red/10 transition-all"
                              onClick={() => handleDeleteCourse(c.id, c.name)}
                              title="Remover Curso"
                            >
                              <Trash2 size={18}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {activeTab === 'turmas' && (classes.length > 0 ? classes.map(t => {
                    const studentsCount = (allEnrollments || []).filter(e => e.turma === t.name && (e.course === t.course || e.courseName === t.course)).length;
                    return (
                      <tr key={t.id}>
                        <td><strong className="text-sm">Turma {t.name}</strong></td>
                        <td><Badge variant="purple" className="text-[9px] font-black uppercase tracking-widest">{t.course}</Badge></td>
                        <td><Badge variant="blue">{t.period || 'Integral'}</Badge></td>
                        <td><span className="font-mono font-black">{studentsCount} Alunos</span></td>
                        <td><span className="text-xs text-muted">Máx: {t.maxStudents || 20}</span></td>
                        <td>
                          <div className="flex gap-2">
                            <button 
                              className="p-2.5 bg-white/5 rounded-xl hover:text-purple hover:bg-purple/10 transition-all"
                              onClick={() => handleEditTurma(t)}
                              title="Editar Turma"
                            >
                              <Edit3 size={18}/>
                            </button>
                            <button 
                              className="p-2.5 bg-white/5 rounded-xl hover:text-red hover:bg-red/10 transition-all"
                              onClick={() => handleDeleteTurma(t.id, t.name, t.course)}
                              title="Excluir Turma"
                            >
                              <Trash2 size={18}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="6" className="text-center py-20 opacity-30 font-black uppercase tracking-widest">Nenhuma turma criada</td></tr>
                  ))}

                  {activeTab === 'audit' && (filteredAudit.length > 0 ? filteredAudit.map(log => (
                    <tr key={log.id}>
                      <td>
                        <div className="flex flex-col">
                          <strong className="text-xs text-white">{log.user}</strong>
                          <span className="text-[9px] text-muted font-mono">{log.userId}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-purple animate-pulse" />
                          <span className="text-xs font-bold text-white/80">{log.action}</span>
                        </div>
                      </td>
                      <td><span className="text-[10px] font-black text-muted uppercase tracking-[2px]">{log.date}</span></td>
                      <td><span className="text-[10px] font-mono text-purple font-black">{log.time}</span></td>
                      <td>
                        <button 
                          className="p-2 bg-white/5 rounded-lg hover:text-red hover:bg-red/10 transition-all"
                          onClick={() => removeAuditLog(log.id)}
                          title="Remover registro"
                        >
                          <Trash2 size={14}/>
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-center py-20 opacity-30 font-black uppercase tracking-widest">Sem registros de auditoria</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>

      {/* Turma Modal */}
      {createPortal(
        <AnimatePresence>
          {showTurmaModal && (
            <div className="modalOverlay" style={{ zIndex: 10001 }}>
              <motion.div 
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="modal"
                style={{ maxWidth: '500px' }}
              >
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h3 className="text-2xl font-black mb-1">{selectedTurma ? 'Editar Turma' : 'Nova Turma'}</h3>
                    <p className="text-sm text-muted font-medium">Configure os detalhes da turma</p>
                  </div>
                  <button onClick={() => setShowTurmaModal(false)} className="text-muted hover:text-white transition-colors">
                    <XCircle size={28}/>
                  </button>
                </div>

                <form onSubmit={handleSaveTurma} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Identificador (Ex: A, B, C)</label>
                      <input 
                        type="text" 
                        value={turmaFormData.name}
                        onChange={e => setTurmaData({...turmaFormData, name: e.target.value.toUpperCase()})}
                        className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold"
                        style={{ backgroundColor: '#070d19' }}
                        placeholder="Ex: A"
                        required
                        maxLength={5}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Curso</label>
                      <select 
                        value={turmaFormData.course}
                        onChange={e => setTurmaData({...turmaFormData, course: e.target.value})}
                        className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold appearance-none"
                        style={{ backgroundColor: '#070d19' }}
                      >
                        {courses.map(c => (
                          <option key={c.id} value={c.name} style={{ backgroundColor: '#0b1322' }}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Período</label>
                      <select 
                        value={turmaFormData.period}
                        onChange={e => setTurmaData({...turmaFormData, period: e.target.value})}
                        className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold appearance-none"
                        style={{ backgroundColor: '#070d19' }}
                      >
                        <option value="Matutino" style={{ backgroundColor: '#0b1322' }}>Matutino</option>
                        <option value="Vespertino" style={{ backgroundColor: '#0b1322' }}>Vespertino</option>
                        <option value="Noturno" style={{ backgroundColor: '#0b1322' }}>Noturno</option>
                        <option value="Integral" style={{ backgroundColor: '#0b1322' }}>Integral</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Vagas Máximas</label>
                      <input 
                        type="number" 
                        value={turmaFormData.maxStudents}
                        onChange={e => setTurmaData({...turmaFormData, maxStudents: parseInt(e.target.value)})}
                        className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold"
                        style={{ backgroundColor: '#070d19' }}
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="modalActions" style={{ marginTop: '48px', display: 'flex', gap: '16px' }}>
                    <Button type="button" variant="outline" className="flex-1 py-4 rounded-2xl font-black" onClick={() => setShowTurmaModal(false)}>CANCELAR</Button>
                    <Button type="submit" className="flex-1 py-4 rounded-2xl font-black shadow-lg shadow-purple/20">
                      {selectedTurma ? 'SALVAR ALTERAÇÕES' : 'CRIAR TURMA'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Course Modal */}
      {createPortal(
        <AnimatePresence>
          {showCourseModal && (
            <div className="modalOverlay" style={{ zIndex: 10002 }}>
              <motion.div 
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="modal"
                style={{ maxWidth: '600px' }}
              >
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h3 className="text-2xl font-black mb-1">{selectedCourse ? 'Editar Curso' : 'Novo Curso'}</h3>
                    <p className="text-sm text-muted font-medium">Configure os detalhes do curso</p>
                  </div>
                  <button onClick={() => setShowCourseModal(false)} className="text-muted hover:text-white transition-colors">
                    <XCircle size={28}/>
                  </button>
                </div>

                <form onSubmit={handleSaveCourse} className="space-y-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Nome do Curso</label>
                    <input 
                      type="text" 
                      value={courseFormData.name}
                      onChange={e => setCourseFormData({...courseFormData, name: e.target.value})}
                      className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold"
                      style={{ backgroundColor: '#070d19' }}
                      placeholder="Ex: Engenharia de Software"
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Descrição</label>
                    <textarea 
                      value={courseFormData.description}
                      onChange={e => setCourseFormData({...courseFormData, description: e.target.value})}
                      className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold"
                      style={{ backgroundColor: '#070d19' }}
                      placeholder="Descrição do curso..."
                      rows={3}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Status</label>
                      <select 
                        value={courseFormData.status}
                        onChange={e => setCourseFormData({...courseFormData, status: e.target.value})}
                        className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold appearance-none"
                        style={{ backgroundColor: '#070d19' }}
                      >
                        <option value="Ativo" style={{ backgroundColor: '#0b1322' }}>Ativo</option>
                        <option value="Inativo" style={{ backgroundColor: '#0b1322' }}>Inativo</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Turmas Disponíveis</label>
                    <div className="grid grid-cols-2 gap-3">
                      {['A', 'B', 'C', 'D'].map(turmaId => {
                        const isSelected = courseFormData.turmas.some(t => t.turmaId === turmaId);
                        const turmaData = courseFormData.turmas.find(t => t.turmaId === turmaId);
                        return (
                          <div key={turmaId} className="flex items-center gap-3 p-3 bg-[#070d19] border border-white/10 rounded-2xl">
                            <input 
                              type="checkbox" 
                              checked={isSelected}
                              onChange={() => toggleTurmaSelection(turmaId)}
                              className="w-4 h-4 rounded accent-purple"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-bold">Turma {turmaId}</div>
                              {isSelected && (
                                <div className="mt-1">
                                  <input 
                                    type="number" 
                                    value={turmaData?.vacancies || 20}
                                    onChange={e => updateTurmaVacancies(turmaId, e.target.value)}
                                    className="w-full bg-transparent border border-white/20 rounded-lg p-2 text-xs"
                                    min="1"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="modalActions" style={{ marginTop: '48px', display: 'flex', gap: '16px' }}>
                    <Button type="button" variant="outline" className="flex-1 py-4 rounded-2xl font-black" onClick={() => setShowCourseModal(false)}>CANCELAR</Button>
                    <Button type="submit" className="flex-1 py-4 rounded-2xl font-black shadow-lg shadow-purple/20">
                      {selectedCourse ? 'SALVAR ALTERAÇÕES' : 'CRIAR CURSO'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Edit Modal (Simulated) */}
      {createPortal(
        <AnimatePresence>
          {showEditModal && (
            <div className="modalOverlay" style={{ zIndex: 10000 }}>
              <motion.div 
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="modal"
                style={{ maxWidth: '600px' }}
              >
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h3 className="text-2xl font-black mb-1">Editar Registro</h3>
                    <p className="text-sm text-muted font-medium">{selectedItem?.fullName} • {selectedItem?.enrollmentId}</p>
                  </div>
                  <button onClick={() => setShowEditModal(false)} className="text-muted hover:text-white transition-colors">
                    <XCircle size={28}/>
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Nome Completo</label>
                      <input 
                        type="text" 
                        defaultValue={selectedItem?.fullName} 
                        className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold"
                        style={{ backgroundColor: '#070d19' }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-2">
                        <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Telefone</label>
                        <input 
                          type="text" 
                          defaultValue={selectedItem?.phone} 
                          className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold"
                          style={{ backgroundColor: '#070d19' }}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[10px] uppercase font-black tracking-widest text-muted ml-1">Status</label>
                        <select 
                          className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold appearance-none"
                          style={{ backgroundColor: '#070d19' }}
                        >
                          <option value="Ativo" style={{ backgroundColor: '#0b1322' }}>Ativo</option>
                          <option value="Suspenso" style={{ backgroundColor: '#0b1322' }}>Suspenso</option>
                          <option value="Trancado" style={{ backgroundColor: '#0b1322' }}>Trancado</option>
                          <option value="Cancelado" style={{ backgroundColor: '#0b1322' }}>Cancelado</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modalActions" style={{ marginTop: '48px', display: 'flex', gap: '16px' }}>
                  <Button variant="outline" className="flex-1 py-4 rounded-2xl font-black" onClick={() => setShowEditModal(false)}>CANCELAR</Button>
                  <Button className="flex-1 py-4 rounded-2xl font-black shadow-lg shadow-purple/20" onClick={() => { setShowEditModal(false); addToast('Dados atualizados com sucesso!', 'success'); }}>SALVAR ALTERAÇÕES</Button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  );
}
